import React, { useState } from 'react';
import EffortRow from './EffortRow';
import { entryHints } from './entryHints';
import { groupTimesheet, dayLabel, weekLabel } from './timesheetGroups';
import { EmptyState, fmtClock } from './vendor';

/**
 * The timesheet worklist, Toggl-style: entries grouped by week, then by day, newest
 * first. Each week is a COLLAPSIBLE panel — its blue header is a toggle. On first
 * load only the current week ("This week") is open; every other week is collapsed,
 * and each can be opened or closed independently. Human and agent entries share the
 * list.
 *
 * Zero entries is a genuine SUCCESS state (the running timer is new) — the empty
 * state names what is absent and shows no spinner.
 *
 * @param {object[]} entries fn_my_timesheet entries.
 * @param {(entry:object)=>void} [onEdit]   Opens the correction flow.
 * @param {(entry:object)=>void} [onStart]  Starts a fresh timer from an entry.
 * @param {(entry:object)=>Promise<{status:'saved'|'failed', message?:string}>} [onDuplicate]
 *   Logs a copy of an entry.
 * @param {(entry:object, change:{minutes:number, description:string})=>Promise<{status:'saved'|'failed', message?:string}>} [onCorrect]
 *   Saves an inline edit of a human row.
 * @param {boolean} [editable] Whether the viewer may change these entries (their own
 *   timesheet). Defaults to false.
 * @param {boolean} [locked] A write is in flight and not yet refetched: every row
 *   disables its inputs, menu and Edit/Verify. Defaults to false.
 * @param {object} [deletion] The delete capability's context (`onDelete`, `undoable`),
 *   handed to each row unchanged. Present only when the capability is on. See EffortRow.
 * @param {{running: object|null, shell: object|null, now: number}} [hintContext] When given, each
 *   human row of an editable timesheet gets advisory "looks invalid" hints, computed here
 *   from the whole list (see entryHints).
 * @param {object} [move] The move controls' context (date bounds, loaders, whether an
 *   overlap check is running). Present only when the move capability is on; handed to
 *   each row unchanged. See EffortRow.
 */
const EmptyClockIcon = () => (
  <svg className="h-8 w-8 text-gray-300 dark:text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);

// Chevron that points down when the panel is open, right when collapsed.
const Chevron = ({ open }) => (
  <svg
    className={`h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-0' : '-rotate-90'}`}
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const TotalPair = ({ label, seconds, bold, onBrand }) => (
  <div className="flex items-center gap-2 text-sm whitespace-nowrap">
    <span className={onBrand ? 'text-white/70' : 'text-gray-400 dark:text-gray-500'}>{label}</span>
    <span className={`tabular-nums ${onBrand ? 'text-white' : 'text-gray-900 dark:text-gray-100'} ${bold ? 'font-bold' : 'font-semibold'}`}>{fmtClock(seconds)}</span>
  </div>
);

/**
 * One collapsible week panel. Open state is local (uncontrolled) and seeded from
 * `defaultOpen`, so collapsing one week never disturbs another.
 */
function WeekSection({ week, defaultOpen, onEdit, onStart, onDuplicate, onCorrect, editable, locked, move, deletion, hintsById }) {
  const [open, setOpen] = useState(defaultOpen);
  const label = weekLabel(week.key);
  const bodyId = `week-body-${week.key}`;

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm" data-testid="timesheet-week">
      {/* Week header — the solid brand-blue bar (same blue as the buttons) doubling
          as the collapse toggle, white text, with the week's running total. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={bodyId}
        className="w-full flex items-center justify-between gap-3 px-4 py-2.5 bg-[#32B1FF] hover:bg-[#2ba2ea] border-b border-[#2175A8] text-left"
        data-testid="week-toggle"
      >
        <span className="flex items-center gap-2 text-white">
          <Chevron open={open} />
          <span className="text-sm font-semibold">{label}</span>
        </span>
        <TotalPair label="Week total" seconds={week.minutes * 60} bold onBrand />
      </button>

      {open && (
        <div id={bodyId}>
          {week.days.map((day) => (
            <div key={day.key} data-testid="timesheet-day">
              {/* Day header — a light blue bar so Today / Yesterday / a date reads
                  apart from the white entry rows (lighter than the week header). */}
              <div className="flex items-center justify-between gap-3 px-4 py-2 bg-sky-50 dark:bg-sky-900/25 border-b border-sky-100 dark:border-sky-900/40">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">{dayLabel(day.key)}</h4>
                <TotalPair label="Total" seconds={day.minutes * 60} />
              </div>
              {/* Rows */}
              <div className="bg-white dark:bg-gray-900">
                {day.entries.map((entry, i) => (
                  <EffortRow
                    key={entry?.id ?? `${day.key}-${i}`}
                    entry={entry}
                    onEdit={onEdit}
                    onStart={onStart}
                    onDuplicate={onDuplicate}
                    onCorrect={onCorrect}
                    editable={editable}
                    locked={locked}
                    move={move}
                    deletion={deletion}
                    hints={hintsById?.get(entry?.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function Timesheet({ entries, onEdit, onStart, onDuplicate, onCorrect, editable = false, locked = false, move, deletion, hintContext }) {
  // Empty is decided on the grouped result: a range holding only superseded originals
  // has nothing to show either.
  const weeks = groupTimesheet(entries);
  // Hints are for the person who can act on them: their own human rows.
  const hintsById = editable && hintContext ? entryHints(entries, hintContext) : null;
  if (weeks.length === 0) {
    return <EmptyState variant="card" title="Timesheet" message="No time logged yet" icon={<EmptyClockIcon />} />;
  }

  // Only the current week ("This week") is open on first load. When there is no
  // current-week data, fall back to the newest week present so something is open.
  const now = new Date();
  const defaultOpenKey =
    weeks.find((w) => weekLabel(w.key, now) === 'This week')?.key ?? weeks[0]?.key;

  return (
    <div className="space-y-4" data-testid="timesheet">
      {weeks.map((week) => (
        <WeekSection
          key={week.key}
          week={week}
          defaultOpen={week.key === defaultOpenKey}
          onEdit={onEdit}
          onStart={onStart}
          onDuplicate={onDuplicate}
          onCorrect={onCorrect}
          editable={editable}
          locked={locked}
          move={move}
          deletion={deletion}
          hintsById={hintsById}
        />
      ))}
    </div>
  );
}
